
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from "@angular/common/http";
import { Inject, Injectable, DOCUMENT } from "@angular/core";
import { Router } from "@angular/router";
import {lastValueFrom, Observable, of, throwError} from "rxjs";
import {catchError, map} from "rxjs/operators";
import { environment } from "src/environments/environment";
import { UserProfileSummary } from "src/swagger-gen/gnvert-secured/models";
import { AuthService } from "../auth/auth.service";
import { AppGuardType } from "../enum/AppGuardType";
import { FullyLoggedInStateType } from "../enum/FullyLoggedInStateType";
import { ConsumeDataService } from "./consume-data.service";
import { LoggedInService } from "./loggedIn.service";
import { UserSessionService } from "./session/user-session.service";
import {OKTA_AUTH} from "@okta/okta-angular";
import OktaAuth from "@okta/okta-auth-js";

@Injectable()
export class LoggedInAuthentService extends LoggedInService {
  constructor(
    @Inject(DOCUMENT) private document: any,
    private httpClient: HttpClient,
    public override userSessionService: UserSessionService,
    public override consumeDataService: ConsumeDataService,
    public override authService: AuthService,
    public override router: Router,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,

  ) {
    super(userSessionService, authService, consumeDataService, router);
  }

  public async loggedIn() {
    this.fullyLoggedInState$.next(FullyLoggedInStateType.PENDING);
    this.resultHandlers = [];

    const isLoggedIn = await this.isLoggedIn();
    if (!isLoggedIn) {
      // authent okta => jwt => access_token
      // apimanager (access_token) => token
      // appelle apimanager (token)  => user

      this.addResultHandler(await this.authTokenHandler());
      this.addResultHandler(await this.authTokenExpireAtHandler());
      this.addResultHandler(await this.dataTokenTypeHandler());
      this.addResultHandler(await this.dataTokenHandler());
      this.addResultHandler(await this.authUserHandler());
      if (this.isValidResultHandlers()) {
        this.fullyLoggedInState$.next(FullyLoggedInStateType.SUCCESS);
      }  else if (await this.oktaAuth.isAuthenticated()) {
        this.fullyLoggedInState$.next(FullyLoggedInStateType.SUCCESS);
      }
      else {
        this.userSessionService.cleanSession();
        this.fullyLoggedInState$.next(
          FullyLoggedInStateType.NO_FULLY_LOGGED_IN
        );
      }
    } else {
      this.fullyLoggedInState$.next(FullyLoggedInStateType.SUCCESS);
    }
  }

  /**
   * Check if User is already completely logged
   */
  public async isLoggedIn() {
    this.clearResultIsLoggedInList();

    if (
      environment.authAppGuard !== AppGuardType.NOT_AUTHENTICATED_JUST_LOGGEDIN
    ) {
      this.addResultIsLoggedIn(this.userSessionService.getAuthExpireAt());
      this.addResultIsLoggedIn(this.userSessionService.getAuthToken());

      this.addResultIsLoggedIn(this.userSessionService.getDataTokenType());
      this.addResultIsLoggedIn(this.userSessionService.getDataToken());
    }
    this.addResultIsLoggedIn(this.userSessionService.getUser());

    let isValid = this.isValidResultIsLoggedIn();

    if (isValid) {
      // Sync the stored expireAt from the current token before checking validity.
      // This prevents false negatives during token renewal (race between EVENT_RENEWED
      // updating okta-custom-token and this read of authExpireAtNameInSession).
      await this.authTokenExpireAtHandler();
    }

    return isValid;
  }

  /**
   * Même fonction que isLoggedIn$(),
   * mais celle-ci retourne un Observable.
   * Plus pratique pour la gestion de Refresh token
   * @protected
   */
  public isLoggedIn$() {
    this.clearResultIsLoggedInList();

    if (
        environment.authAppGuard !== AppGuardType.NOT_AUTHENTICATED_JUST_LOGGEDIN
    ) {
      this.addResultIsLoggedIn(this.userSessionService.getAuthExpireAt());
      this.addResultIsLoggedIn(this.userSessionService.getAuthToken());

      this.addResultIsLoggedIn(this.userSessionService.getDataTokenType());
      this.addResultIsLoggedIn(this.userSessionService.getDataToken());
    }
    this.addResultIsLoggedIn(this.userSessionService.getUser());

    const isValid = this.isValidResultIsLoggedIn();


    if (isValid) {
      // if the expiration has changed: the authService may have changed its token
      // So, the user in not logged in (cause: data in localstorage may be unvalid like authToken)
      const oldExpiredAt = this.userSessionService.getAuthExpireAt();
      return this.generateAuthTokenExpireAt$()
          .pipe(
              map(newExpiredAt => {
              return oldExpiredAt === newExpiredAt;
            })
          );
    }

    return of(false);
  }

  public async refreshAuthTokenExireAt() {
    await this.authTokenExpireAtHandler();
  }

  // ----------------- USER ----------------- //
  protected async getUser(): Promise<UserProfileSummary | null> {
    try {
      return await lastValueFrom(this.authorizeUser());
    } catch (error) {
      console.error('Error on getUser() in authorizeUser', error);
      return null;
    }
  }

  /**
   * Check user is authorized
   * @param user AuthorizationUser
   * @param tineaToken token got from Api Manager (oauth)
   */
  authorizeUser(): Observable<UserProfileSummary> {
    const userAuthorizeUrl = environment.apiUrl + "/custom/login";
    const httpOptions = {
      headers: new HttpHeaders()
        .set("Content-Type", "application/json")
        .set("Accept", "application/json"),
    };

    return this.httpClient
      .post<UserProfileSummary>(userAuthorizeUrl, null, httpOptions)
      .pipe(catchError(this.handleServerError));
  }

  protected handleServerError(badResponse: HttpErrorResponse) {
    console.error("An error occurred in authorizeUser()", badResponse);
    return throwError(() => badResponse);
  }
}
